import React, { useState, useEffect } from 'react';
import axios from 'axios';
import ApiUrl from '../../utils/config';
import {
  Participant,
  ParticipantClient,
  ParticipantDto,
  Person,
  PersonClient,
  Tournament,
} from '../../utils/apiClient';
import { ReactSelectDefault } from '../../common/components/ReactSelectDefault/ReactSelect';
import AppContext from '../../AppContext';
import { handleError } from '../../utils';
import {
  formatAgeCategoryLabel,
  formatWeightCategoryLabel,
  resolveWeightCategory,
} from '../../utils/categoryResolver';

interface EditParticipantModalProps {
  isOpen: boolean;
  onClose: () => void;
  participant: Participant;
  tournament: Tournament;
  onSaved: (updated: Participant) => void;
}

const EditParticipantModal: React.FC<EditParticipantModalProps> = ({
  isOpen, onClose, participant, tournament, onSaved
}) => {
  const appContext = React.useContext(AppContext);
  const [coaches, setCoaches] = useState<Person[]>([]);
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);

  const participantClient = new ParticipantClient(ApiUrl, axios);
  const personClient = new PersonClient(ApiUrl, axios);

  const [form, setForm] = useState<ParticipantDto>(new ParticipantDto({
    id: participant.id,
    personId: participant.personId!,
    tournamentId: tournament.id!,
    weight: participant.weight,
    organization: participant.organization,
    coachId: participant.coachId,
    rank: participant.rank || '',
    ageCategoryInTournamentId: participant.ageCategoryInTournamentId,
    weightCategoryInTournamentId: participant.weightCategoryInTournamentId,
  }));

  // Авто-определение весовой категории при изменении веса
  useEffect(() => {
    if (!form.weight || form.weight <= 0) return;
    const matching = resolveWeightCategory(form.weight, tournament.weightCategories ?? []);
    if (matching) {
      setForm(f => new ParticipantDto({ ...f, weightCategoryInTournamentId: matching.id }));
    }
  }, [form.weight]);

  useEffect(() => {
    if (!isOpen) return;
    const load = async () => {
      setLoading(true);
      try {
        const persons = await personClient.getPersons();
        setCoaches(persons.filter(p => p.personRoles?.some(r => r.role?.name === 'Тренер')));
      } catch (ex) {
        handleError(appContext!.setNotificationData, ex);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [isOpen]);

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = form.toJSON();
      payload.coachId = form.coachId ?? null;
      await participantClient.updateParticipant(ParticipantDto.fromJS(payload));
      const refreshed = await participantClient.getParticipants(tournament.id!);
      const updated = refreshed.find(p => p.id === participant.id);
      onSaved(updated ?? participant);
      onClose();
    } catch (ex) {
      handleError(appContext!.setNotificationData, ex, 'Ошибка при сохранении');
    } finally {
      setSaving(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="admin-participant-modal-overlay" onClick={onClose}>
      <div className="admin-participant-modal" onClick={e => e.stopPropagation()}>
        <div className="admin-participant-modal__header">
          <h2>
            Редактирование участника —{' '}
            {participant.person
              ? `${participant.person.lastName} ${participant.person.firstName}`
              : ''}
          </h2>
          <button className="admin-participant-modal__close" onClick={onClose}>×</button>
        </div>

        <div className="admin-participant-modal__content">
          {loading ? (
            <div className="admin-participant-modal__loading">Загрузка...</div>
          ) : (
            <form onSubmit={handleSave} className="admin-participant-modal__form">

              <div className="admin-participant-modal__form-row">
                <div className="admin-participant-modal__form-group">
                  <label>Вес (кг) <span className="required">*</span></label>
                  <input
                    type="number"
                    min="0" max="200" step="0.1"
                    value={form.weight || ''}
                    onChange={e => setForm(new ParticipantDto({ ...form, weight: parseFloat(e.target.value) || 0 }))}
                    required
                  />
                </div>

                <div className="admin-participant-modal__form-group">
                  <label>Спортивная школа <span className="required">*</span></label>
                  <input
                    type="text"
                    value={form.organization}
                    onChange={e => setForm(new ParticipantDto({ ...form, organization: e.target.value }))}
                    required
                  />
                </div>
              </div>

              <div className="admin-participant-modal__form-row">
                <div className="admin-participant-modal__form-group">
                  <label>Разряд</label>
                  <input
                    type="text"
                    value={form.rank || ''}
                    onChange={e => setForm(new ParticipantDto({ ...form, rank: e.target.value }))}
                  />
                </div>

                <div className="admin-participant-modal__form-group">
                  <label>Тренер</label>
                  <ReactSelectDefault<number>
                    options={coaches.map(c => ({
                      value: c.id!,
                      label: `${c.lastName} ${c.firstName} ${c.patronymic || ''}`.trim()
                    }))}
                    value={form.coachId}
                    onChange={opt => setForm(new ParticipantDto({ ...form, coachId: opt?.value || undefined }))}
                    placeholder="Без тренера"
                    params={{ width: 100, height: 42 }}
                    isClearable
                    error={false}
                  />
                </div>
              </div>

              <div className="admin-participant-modal__form-row">
                <div className="admin-participant-modal__form-group">
                  <label>Возрастная категория</label>
                  <ReactSelectDefault<number>
                    options={tournament.ageCategories?.map(ac => ({
                      value: ac.id!,
                      label: formatAgeCategoryLabel(ac, tournament.ageCategories ?? [])
                    })) || []}
                    value={form.ageCategoryInTournamentId}
                    onChange={opt => setForm(new ParticipantDto({ ...form, ageCategoryInTournamentId: opt?.value || undefined }))}
                    placeholder="Не определена"
                    params={{ width: 100, height: 42 }}
                    isClearable
                    error={false}
                  />
                </div>

                <div className="admin-participant-modal__form-group">
                  <label>Весовая категория</label>
                  <ReactSelectDefault<number>
                    options={tournament.weightCategories?.map(wc => ({
                      value: wc.id!,
                      label: formatWeightCategoryLabel(wc, tournament.weightCategories ?? [])
                    })) || []}
                    value={form.weightCategoryInTournamentId}
                    onChange={opt => setForm(new ParticipantDto({ ...form, weightCategoryInTournamentId: opt?.value || undefined }))}
                    placeholder="Не определена"
                    params={{ width: 100, height: 42 }}
                    isClearable
                    error={false}
                  />
                </div>
              </div>

              <div className="admin-participant-modal__actions">
                <button type="button" className="admin-participant-modal__btn admin-participant-modal__btn--cancel" onClick={onClose}>
                  Отмена
                </button>
                <button type="submit" className="admin-participant-modal__btn admin-participant-modal__btn--save" disabled={saving}>
                  {saving ? 'Сохранение...' : 'Сохранить'}
                </button>
              </div>

            </form>
          )}
        </div>
      </div>
    </div>
  );
};

export default EditParticipantModal;
